import {
    Brain,
    GraduationCap,
    ShieldCheck,
    Trophy,
} from "lucide-react";

const performanceStaff = [
    {
        id: "jacob-eastwood",
        name: "Jacob Eastwood",
        role: "Director of Tennis Performance",
        bio: "Jacob has played and coached at high levels of the game, bringing extensive experience in player development, technical instruction, and match strategy. His coaching combines competitive insight with a practical, player-centered approach designed to help athletes improve and perform with confidence.",
        credentials: [
            "B.S. Psychology",
            "PTR Certified",
            "USTA Coaching Member",
            "ARA Coaching Member",
        ],
        playingExperience: [
            "Lamar University",
            "University of North Georgia",
            "Former ITF Professional Circuit Competitor",
        ],
        specialties: [
            "Player Development",
            "Technical Instruction",
            "Match Strategy",
        ],
    },
    {
        id: "aubrey-biddle",
        name: "Aubrey Biddle",
        role: "Director of Human Performance",
        bio: "Aubrey specializes in the science of how athletes learn, move, and perform. Her background in sport psychology, exercise science, education, and applied behavior analysis connects physical performance with the behavioral and learning processes that support lasting improvement.",
        credentials: [
            "B.S. Health & Physical Education, Psychology Minor",
            "M.S. Exercise Science & Health Promotion, Sport Psychology",
            "Postgraduate Certificate in Applied Behavior Analysis — In Progress",
            "PTR Certified",
            "USTA Coaching Member",
        ],
        playingExperience: [
            "University of West Georgia",
        ],
        specialties: [
            "Sport Psychology",
            "Motor Learning",
            "Behavior Change",
        ],
    },
];

export default function FrameworkCoaches() {
    return (
        <div data-testid="framework-coaches-page">
            {/* Full-bleed staff hero */}
            <section className="relative">
                <div className="relative w-full h-[70vh] min-h-[520px] max-h-[820px] overflow-hidden bg-black">
                    <img
                        src="/brand/coaches-serve.png"
                        alt="Framework Tennis performance staff"
                        className="w-full h-full object-contain"
                        style={{ objectPosition: "center bottom" }}
                    />

                    {/* Vignette and fade */}
                    <div
                        className="absolute inset-0 pointer-events-none"
                        style={{
                            background:
                                "radial-gradient(ellipse at center, transparent 40%, rgba(0,0,0,0.55) 100%)",
                        }}
                    />

                    <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-b from-transparent to-black pointer-events-none" />

                    <div className="absolute inset-0 flex flex-col justify-end px-6 pb-12 lg:px-10">
                        <div className="w-full max-w-[1400px] mx-auto">
                            <div className="fw-label mb-3">
                                Performance Staff
                            </div>

                            <h1 className="fw-display text-5xl leading-none sm:text-7xl">
                                We Build{" "}
                                <span className="text-[#a6ce39]">
                                    Better Players.
                                </span>
                            </h1>
                        </div>
                    </div>
                </div>
            </section>

            {/* Staff philosophy */}
            <section className="max-w-[1400px] mx-auto px-6 pt-12 pb-16 lg:px-10">
                <p className="max-w-4xl text-lg leading-relaxed text-white/70 sm:text-xl">
                    Framework coaches are active players, performance specialists,
                    and lifelong students of the game and the science behind human
                    performance. We still put in the reps—on court, in the
                    classroom, and in the lab.
                </p>
            </section>

            <div className="fw-rule max-w-[1400px] mx-auto" />

            {/* Professional standards */}
            <section className="max-w-[1400px] mx-auto px-6 py-16 lg:px-10">
                <div className="fw-card p-6 sm:p-8 lg:p-10">
                    <div className="flex flex-col gap-6 md:flex-row md:items-start">
                        <div className="flex h-12 w-12 shrink-0 items-center justify-center border border-[#a6ce39]/40 bg-[#a6ce39]/10">
                            <ShieldCheck
                                className="text-[#a6ce39]"
                                size={24}
                            />
                        </div>

                        <div className="max-w-4xl">
                            <div className="fw-label mb-3">
                                Our Standards
                            </div>

                            <p className="text-lg leading-relaxed text-white/75">
                                Every Framework coach is committed to
                                professionalism, continued education, and
                                athlete-centered coaching. Our staff maintains
                                professional coaching certifications, liability
                                insurance, and ongoing professional development
                                while fostering a culture of transparency,
                                integrity, and lifelong learning.
                            </p>

                            <p className="mt-4 leading-relaxed text-white/60">
                                We openly share our backgrounds, education,
                                certifications, and experience because we believe
                                trust is built through transparency.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            <div className="fw-rule max-w-[1400px] mx-auto" />

            {/* Staff profiles */}
            <section className="max-w-[1400px] mx-auto grid gap-20 px-6 py-20 lg:px-10">
                {performanceStaff.map((coach, index) => (
                    <article
                        key={coach.id}
                        className="grid gap-10 lg:grid-cols-12 lg:gap-16"
                        data-testid={`fw-coach-${coach.id}`}
                    >
                        {/* Name and biography */}
                        <div className="lg:col-span-5">
                            <div className="fw-mono mb-5 text-xs font-bold uppercase tracking-[0.25em] text-[#a6ce39]">
                                Staff.0{index + 1}
                            </div>

                            <div className="fw-label mb-3">
                                {coach.role}
                            </div>

                            <h2 className="fw-display text-4xl sm:text-5xl">
                                {coach.name}
                            </h2>

                            <p className="mt-6 text-lg leading-relaxed text-white/70">
                                {coach.bio}
                            </p>
                        </div>

                        {/* Credentials and specialties */}
                        <div className="grid gap-6 sm:grid-cols-2 lg:col-span-7">
                            <div className="fw-card p-5 sm:p-6">
                                <GraduationCap
                                    className="mb-4 text-[#a6ce39]"
                                    size={22}
                                />

                                <div className="fw-label mb-3">
                                    Credentials
                                </div>

                                <ul className="space-y-2 text-sm leading-relaxed text-white/70">
                                    {coach.credentials.map((credential) => (
                                        <li
                                            key={credential}
                                            className="flex gap-3"
                                        >
                                            <span
                                                className="text-[#a6ce39]"
                                                aria-hidden="true"
                                            >
                                                —
                                            </span>
                                            <span>{credential}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            <div className="fw-card p-5 sm:p-6">
                                <Trophy
                                    className="mb-4 text-[#a6ce39]"
                                    size={22}
                                />

                                <div className="fw-label mb-3">
                                    Playing Experience
                                </div>

                                <ul className="space-y-2 text-sm leading-relaxed text-white/70">
                                    {coach.playingExperience.map(
                                        (experience) => (
                                            <li
                                                key={experience}
                                                className="flex gap-3"
                                            >
                                                <span
                                                    className="text-[#a6ce39]"
                                                    aria-hidden="true"
                                                >
                                                    —
                                                </span>
                                                <span>{experience}</span>
                                            </li>
                                        ),
                                    )}
                                </ul>
                            </div>

                            <div className="fw-card p-5 sm:col-span-2 sm:p-6">
                                <Brain
                                    className="mb-4 text-[#a6ce39]"
                                    size={22}
                                />

                                <div className="fw-label mb-4">
                                    Specialties
                                </div>

                                <div className="flex flex-wrap gap-2">
                                    {coach.specialties.map((specialty) => (
                                        <span
                                            key={specialty}
                                            className="fw-mono border border-white/20 px-3 py-1.5 text-[10px] uppercase tracking-[0.25em] text-white/80"
                                        >
                                            {specialty}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </article>
                ))}
            </section>
        </div>
    );
}